import { useEffect, useRef, useState } from "react"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import {
  fetchTrendTopTracks,
  fetchStreamsTrackMonths,
  fetchArtistTrendEconomyData,
  fetchSummaryTrackRoyalties,
  fetchSummaryTrackStreams
} from "../../../actions/trendsActions"
import { AdminTrendsProps, ArtistTrendEconomy, SortParams, SummaryTrackRoyalty, TrendFilter } from "../../../types"
import { KButton, KDropdown, KInput, KSelectRange, KSelectRangeDate, KSpan } from "kahuna-base-react-components"
import Loading from "../../ui/Loading"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { toast } from "react-toastify"
import {
  ADMIN_PERMISSIONS,
  allPlatformOptions,
  errorFilledToastOptions,
  primitivePlatformOptions,
  successFilledToastOptions,
  trendsIcons
} from "../../../constants"
import { capitalize, checkAdminPermission } from "../../../utility"
// @ts-ignore
import SpotifyLogo from "../../../assets/release-platform-icons/SpotifyLogo.png"
import StreamsTopTracks from "./streams-top-tracks/StreamsTopTracks"
import StreamsTrackMonths from "./streams-track-months/StreamsTrackMonths"
import RoyaltyThreeMonths from "./royalty-three-months/RoyaltyThreeMonths"
import { platformValueMap } from "./Trends"
import { lang } from "../../../constants/languages"
import SummaryTrackRoyalties, { getSummaryTrackRoyaltyKey } from "./summary-track-royalty/SummaryTrackRoyalties"
import SummaryTrackStreams from "./summary-track-stream/SummaryTrackStreams"
import { useSearchParams } from "react-router-dom"
import { discoveryMode, trends } from "../../../apis"

enum TabOptions {
  STREAMS_TOP_TRACKS,
  STREAMS_TRACK_MONTHS,
  ROYALTY_THREE_MONTHS,
  TRACK_ROYALTY_SUMMARY,
  TRACK_STREAM_SUMMARY
}

const TRACK_ROYALTY_SUMMARY_TAB_PARAM = "track-royalty-summary"
const SPOTIFY_PLATFORM = "spo-spotify"
const DISTRIBUTION_TYPE_NON_ADVANCE = "distribution"
const DISTRIBUTION_TYPE_ADVANCE = "advance"
// Low enough that smaller catalogs losing revenue are listed as well
const DECLINING_MIN_ROYALTY = "10"

enum PerformanceOptions {
  ALL,
  DECLINING_NON_ADVANCE,
  DECLINING_ADVANCE
}

// The declining threshold is owned by the backend and comes with the filter defaults
const getPerformanceOptions = (decliningPercentage?: number): KSelectOption[] => [
  { label: "All Tracks", value: PerformanceOptions.ALL },
  {
    label: decliningPercentage ? `Declining >${decliningPercentage}% (Non-Advance)` : "Declining (Non-Advance)",
    value: PerformanceOptions.DECLINING_NON_ADVANCE
  },
  {
    label: decliningPercentage ? `Declining >${decliningPercentage}% (Advance)` : "Declining (Advance)",
    value: PerformanceOptions.DECLINING_ADVANCE
  }
]

type RoyaltySummaryFilterDefaults = {
  date: Date | null
  decliningPercentage?: number
}

// "YYYYMM" -> first day of that month
const parseYearMonth = (value?: string | null) => {
  if (!value || !/^\d{6}$/.test(value)) return null
  return new Date(Number(value.slice(0, 4)), Number(value.slice(4, 6)) - 1, 1)
}

const initialSort: SortParams = {
  field: "",
  direction: undefined
}

// THIS COMPONENT IS SPECIFIC TO ADMIN PANEL. NEVER USE IT IN ARTIST PANEL PAGES.
const AdminTrends = ({
  trendTopTracks,
  fetchTrendTopTracks,
  fetchStreamsTrackMonths,
  artistTrendEconomyData,
  fetchArtistTrendEconomyData,
  summaryTrackRoyalties,
  fetchSummaryTrackRoyalties,
  summaryTrackStreams,
  fetchSummaryTrackStreams,
  options,
  user,
  selectedUsers
}: AdminTrendsProps) => {
  const [searchParams] = useSearchParams()
  const [selectedTab, setSelectedTab] = useState<TabOptions | undefined>(undefined)
  const [filter, setFilter] = useState<TrendFilter>({
    dateValues: [null, null],
    isrcList: [],
    countryList: [],
    ageGroupList: [],
    sexList: [],
    platformList: [],
    artistList: [],
    isAdmin: true,
    minRoyalty: "100",
    minStream: "100000"
  })
  const [platformOptions, setPlatformOptions] = useState<KSelectOption[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<KSelectOption[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchOptions, setFetchOptions] = useState(true)
  const [offset, setOffset] = useState<number>(0)
  const [limit, setLimit] = useState<number>(20)
  const [selectedRoyaltyType, setSelectedRoyaltyType] = useState<KSelectOption>()
  const [selectedResults, setSelectedResults] = useState<ArtistTrendEconomy[]>([])
  const [hasNext, setHasNext] = useState<boolean>(false)
  const twoDaysAgo = new Date()
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)
  const formattedDate = twoDaysAgo.toISOString().slice(0, 10).replace(/-/g, "") // "YYYYMMDD"
  const [date, setDate] = useState(formattedDate)
  const [royaltySummaryDefaults, setRoyaltySummaryDefaults] = useState<RoyaltySummaryFilterDefaults>()

  const [sort, setSort] = useState<SortParams>(initialSort)
  const [discoveryModeRowKeys, setDiscoveryModeRowKeys] = useState<string[]>([])
  const [discoveryModeSubmitting, setDiscoveryModeSubmitting] = useState(false)
  const [selectedPerformanceOption, setSelectedPerformanceOption] = useState<KSelectOption>(
    getPerformanceOptions(royaltySummaryDefaults?.decliningPercentage)[0]
  )

  // A new result list drops the rows picked from the previous one
  useEffect(() => {
    setDiscoveryModeRowKeys([])
  }, [summaryTrackRoyalties])

  const toggleDiscoveryModeRows = (rows: SummaryTrackRoyalty[], selected: boolean) => {
    const rowKeys = rows.map(getSummaryTrackRoyaltyKey)
    setDiscoveryModeRowKeys((previousKeys) =>
      selected
        ? Array.from(new Set([...previousKeys, ...rowKeys]))
        : previousKeys.filter((key) => !rowKeys.includes(key))
    )
  }

  // Low performers are sent to the open Discovery Mode campaign by hand, credited to the account they are listed under
  const submitDiscoveryMode = () => {
    if (!window.confirm("Are you sure you want to add the selected tracks to Discovery Mode?")) {
      return
    }
    const items = summaryTrackRoyalties
      .filter((row) => discoveryModeRowKeys.includes(getSummaryTrackRoyaltyKey(row)))
      .map((row) => ({ user_id: row.userId, track_id: row.trackId }))
    if (items.length === 0) return

    setDiscoveryModeSubmitting(true)
    discoveryMode("/admin_submit/", "post", { items })
      .then((response) => {
        const { added_count, already_selected_count, ignored, campaign_label } = response.data
        const skipped = [
          already_selected_count ? `${already_selected_count} already selected` : "",
          ignored?.length ? `${ignored.length} not eligible` : ""
        ].filter(Boolean)
        const skippedText = skipped.length ? ` (${skipped.join(", ")})` : ""
        if (!added_count) {
          // Not eligible means the track is not in an active release of the account it is listed under
          toast.error(`No tracks added to Discovery Mode${skippedText}`, errorFilledToastOptions)
          return
        }
        toast.success(
          `${added_count} track(s) added to the ${campaign_label} Discovery Mode campaign${skippedText}`,
          successFilledToastOptions
        )
        applySummaryFilter()
      })
      .catch(() => toast.error("Tracks could not be added to Discovery Mode", errorFilledToastOptions))
      .finally(() => setDiscoveryModeSubmitting(false))
  }

  const handleSort = (field: string) => {
    let sortField = sort.field
    let sortDirection: "asc" | "desc" | undefined = sort.direction

    if (sortField === field) {
      if (!sortDirection) {
        sortDirection = "desc"
      } else if (sortDirection === "desc") {
        sortDirection = "asc"
      } else {
        sortDirection = undefined
        sortField = ""
      }
    } else {
      sortField = field
      sortDirection = "desc"
    }
    setSort({ field: sortField, direction: sortDirection })
  }

  const useTimeout = (timeoutInMs: number) => {
    setTimeout(() => {
      setLoading(false)
    }, timeoutInMs)
  }

  useEffect(() => {
    if (artistTrendEconomyData.results.length === limit) {
      setHasNext(true)
    } else {
      setHasNext(false)
    }

    setSelectedResults([...selectedResults, ...artistTrendEconomyData.results])
  }, [artistTrendEconomyData.results])

  useEffect(() => {
    if (selectedTab === undefined) return

    if (selectedTab === TabOptions.STREAMS_TOP_TRACKS) {
      setLoading(true)
      fetchTrendTopTracks(date, true, filter).then(() => {
        useTimeout(200)
      })
      return
    }

    if (selectedTab === TabOptions.STREAMS_TRACK_MONTHS) {
      setLoading(true)
      fetchStreamsTrackMonths(filter, offset, limit, selectedRoyaltyType).then(() => {
        useTimeout(100)
      })
      return
    }

    if (selectedTab === TabOptions.ROYALTY_THREE_MONTHS) {
      setLoading(true)
      fetchArtistTrendEconomyData(filter, offset, limit, selectedRoyaltyType).then(() => {
        useTimeout(100)
      })
      return
    }
    setFetchOptions(false)
  }, [filter, offset, limit, selectedRoyaltyType])

  const applySummaryFilter = (appliedFilter: TrendFilter = filter, tab: TabOptions | undefined = selectedTab) => {
    if (tab === TabOptions.TRACK_ROYALTY_SUMMARY) {
      setLoading(true)
      fetchSummaryTrackRoyalties(appliedFilter, sort).then(() => {
        useTimeout(200)
      })
    }

    if (tab === TabOptions.TRACK_STREAM_SUMMARY) {
      setLoading(true)
      fetchSummaryTrackStreams(appliedFilter, sort).then(() => {
        useTimeout(200)
      })
    }
  }

  const fetchRoyaltySummaryDefaults = async () => {
    try {
      const response = await trends(`/summary_track_royalties_filter_defaults/?platforms=${SPOTIFY_PLATFORM}`, "get")
      const defaults: RoyaltySummaryFilterDefaults = {
        date: parseYearMonth(response.data?.date),
        decliningPercentage: response.data?.declining_percentage
      }
      setRoyaltySummaryDefaults(defaults)
      return defaults
    } catch (e) {
      return undefined
    }
  }

  useEffect(() => {
    if (selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY && !royaltySummaryDefaults) {
      fetchRoyaltySummaryDefaults()
    }
  }, [selectedTab])

  // Spotify on the latest month that has royalty data, limited to non-advance catalogs going down
  const applyDeclining = async (isAdvance: boolean, date?: Date | null) => {
    const platforms = [SPOTIFY_PLATFORM]
    setLoading(true)
    const month = date || (royaltySummaryDefaults || (await fetchRoyaltySummaryDefaults()))?.date
    const decliningFilter: TrendFilter = {
      ...filter,
      // filter may still be the initial one on a deep link, before the selectedUsers effect has run
      selectedUsers: selectedUsers.map((selectedUser) => selectedUser.value),
      platformList: platforms,
      minRoyalty: DECLINING_MIN_ROYALTY,
      dateValues: month ? [month, month] : filter.dateValues,
      declining: true,
      distributionType: isAdvance ? DISTRIBUTION_TYPE_ADVANCE : DISTRIBUTION_TYPE_NON_ADVANCE
    }
    setFilter(decliningFilter)
    setSelectedPlatforms(allPlatformOptions.filter((option) => platforms.includes(option.value2 || "")))
    applySummaryFilter(decliningFilter, TabOptions.TRACK_ROYALTY_SUMMARY)
  }

  const clearDeclining = () => {
    setFilter((prevFilter) => ({ ...prevFilter, declining: false, distributionType: undefined }))
  }

  useEffect(() => {
    // Deep link from the bot-summaries Slack channel once Spotify reports are imported
    if (searchParams.get("tab") !== TRACK_ROYALTY_SUMMARY_TAB_PARAM) return
    if (!checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_ROYALTY_SUMMARY, user?.adminPermissions)) return

    setSelectedTab(TabOptions.TRACK_ROYALTY_SUMMARY)
    if (searchParams.get("declining") === "1") {
      applyDeclining(false, parseYearMonth(searchParams.get("date")))
      setSelectedPerformanceOption(getPerformanceOptions(royaltySummaryDefaults?.decliningPercentage)[1])
    }
  }, [])

  const processPlatformOptions = (platformOptions: string[]) => {
    return platformOptions.map((platform: string, index: number) => {
      return {
        label: capitalize(platform),
        value: platformValueMap?.[platform as keyof typeof platformValueMap] || index + 20,
        value2: platform,
        icon: trendsIcons[platform as keyof typeof platformValueMap],
        iconLabel: platform === "spotify" && SpotifyLogo
      }
    })
  }

  useEffect(() => {
    const userIds = selectedUsers.map((selectedUser) => selectedUser.value)
    setTimeout(() => {
      setSelectedResults([])
      setOffset(0)
      setFetchOptions(true)
      setFilter((prevFilter) => ({ ...prevFilter, selectedUsers: userIds }))
    }, 100)
  }, [selectedUsers])

  useEffect(() => {
    if (!options) {
      return
    }

    const platformOptions = options.platformOptions || []

    const platformOptionsProcessed = processPlatformOptions(platformOptions)

    if (filter.platformList.length === 0) {
      // selectedPlatforms used to show all platforms selected when no filter.platformList applied,
      // it is canceled in new logic. selectedPlatforms state might be redundant, but not deleted for now.
      //setSelectedPlatforms(platformOptionsProcessed)
      setSelectedPlatforms([])
    }

    setPlatformOptions(platformOptionsProcessed)
  }, [options])

  const findSelectedValue = (_key: string, options: KSelectOption[]) => {
    return options.filter((option) => filter[_key].includes(option.value2))
  }

  const multipleDropdownComponent = (
    title: string,
    options: KSelectOption[],
    _key: string,
    showOnlyIconsInMulti: boolean,
    width: number,
    sortSelectedFirst: boolean,
    reFetchOptions: boolean,
    dataTestId?: string
  ) => {
    return (
      <div>
        <div className="mb-1">
          <KSpan text={title} />
        </div>
        <div data-testid={dataTestId}>
          <KDropdown
            isClearable={true}
            isMulti={true}
            showOnlyIconsInMulti={showOnlyIconsInMulti}
            width={width}
            closeMenuOnSelect={false}
            options={options}
            onSelect={() => {}}
            onBlur={(selected) => {
              if (reFetchOptions) {
                setFetchOptions(true)
              }
              const value2List = (selected as KSelectOption[])?.map((entity) => entity.value2 || "") || []
              setFilter((prevFilter) => {
                if (value2List?.toString() === prevFilter[_key]?.toString()) {
                  return prevFilter
                }
                return { ...prevFilter, [_key]: value2List }
              })
              if (_key === "platformList") {
                setSelectedPlatforms(selected as KSelectOption[])
              }
            }}
            placeholder={lang.trends.filter.all}
            selected={
              // selectedPlatforms is reset when the platform options load, so a preset platformList
              // (e.g. Spotify from the declining filter) is still shown from the filter itself
              _key === "platformList" && selectedPlatforms.length > 0
                ? selectedPlatforms
                : findSelectedValue(_key, options)
            }
            sortSelectedFirst={sortSelectedFirst}
          />
        </div>
      </div>
    )
  }

  const resetFilters = () => {
    setFilter({
      ...filter,
      ageGroupList: [],
      isrcList: [],
      artistList: [],
      sexList: [],
      countryList: [],
      platformList: [],
      declining: false,
      distributionType: undefined
    })
    setSelectedPlatforms([])
  }

  const toLocaleDate = (date: Date) => {
    if (selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY) {
      return date.toLocaleDateString("en-US", { month: "short", year: "numeric" })
    } else {
      return date.toLocaleDateString("en-US", { month: "short", year: "numeric", day: "2-digit" })
    }
  }

  const renderFilters = () => {
    if (selectedTab === undefined) {
      return (
        <div className="mt-10">
          <KSpan text="Select to view" />
        </div>
      )
    }

    if (selectedTab === TabOptions.ROYALTY_THREE_MONTHS || selectedTab === TabOptions.STREAMS_TRACK_MONTHS) {
      return <div className="mb-4 flex mt-10"></div>
    }

    let filteredPlatformOption: KSelectOption[] = platformOptions
    if (selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY) filteredPlatformOption = allPlatformOptions
    else if (selectedTab === TabOptions.TRACK_STREAM_SUMMARY) filteredPlatformOption = primitivePlatformOptions

    const platformSelection = multipleDropdownComponent(
      lang.trends.filter.platforms,
      filteredPlatformOption,
      "platformList",
      true,
      175,
      false,
      true,
      "platform-dropdown"
    )

    if (selectedTab === TabOptions.STREAMS_TOP_TRACKS) {
      return (
        <div className="flex gap-4 mt-10">
          <div>{platformSelection}</div>
          <div className="mb-10">
            <div className="mb-1">
              <KSpan text={"Date"} />
            </div>
            <div>
              <div className="flex gap-2 mb-4 flex-wrap">
                <KInput
                  onChange={(text) => setDate(text)}
                  placeholder="Ex: 20250322"
                  value={date}
                  onBlur={(value) => {
                    if (value.length !== 8) {
                      toast.error("Wrong date format. Example: 20250322", errorFilledToastOptions)
                      return
                    }
                    fetchTrendTopTracks(value, true, filter).then(() =>
                      toast.success(`Success Date: ${date}`, successFilledToastOptions)
                    )
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY || selectedTab === TabOptions.TRACK_STREAM_SUMMARY) {
      const performanceOptions = getPerformanceOptions(royaltySummaryDefaults?.decliningPercentage)
      return (
        <div className="flex justify-between  mt-8 mb-4">
          <div className="flex gap-4">
            {selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY && (
              <div>
                <div className="mb-1">
                  <KSpan text="Performance" />
                </div>
                <div data-testid="performance-dropdown">
                  <KDropdown
                    width={290}
                    options={performanceOptions}
                    selected={selectedPerformanceOption}
                    onSelect={(selected) => {
                      setSelectedPerformanceOption(selected as KSelectOption)
                      if ((selected as KSelectOption)?.value === PerformanceOptions.DECLINING_NON_ADVANCE) {
                        applyDeclining(false)
                      } else if ((selected as KSelectOption)?.value === PerformanceOptions.DECLINING_ADVANCE) {
                        applyDeclining(true)
                      } else {
                        clearDeclining()
                      }
                    }}
                  />
                </div>
              </div>
            )}
            <div>{platformSelection}</div>
            <div className="grid gap-1">
              <KSpan text={selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? "Min Royalty (USD)" : "Min Stream"} />
              <KInput
                value={selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? filter.minRoyalty : filter.minStream}
                onChange={(value) =>
                  selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY
                    ? setFilter((prevFilter) => ({ ...prevFilter, minRoyalty: value }))
                    : setFilter((prevFilter) => ({ ...prevFilter, minStream: value }))
                }
              />
            </div>
            <div className="grid gap-1">
              <KSpan text="Date" />
              <div className="flex gap-2">
                {selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? (
                  <div className="min-w-9">
                    <KSelectRange
                      value={filter.dateValues}
                      onChange={(values) => {
                        if (!values || !values?.[0]) {
                          return
                        }
                        setFilter((prevFilter) => ({ ...prevFilter, dateValues: values }))
                      }}
                      height="36px"
                      width="auto"
                      backgroundColor="#F7F7F7"
                      hoverBackgroundColor="#F3F3F3"
                      boxShadow="none"
                    />
                  </div>
                ) : (
                  <div className="min-w-9">
                    <KSelectRangeDate
                      value={filter.dateValues}
                      onChange={(values) => {
                        if (!values || !values?.[0]) {
                          return
                        }
                        setFilter((prevFilter) => ({ ...prevFilter, dateValues: values }))
                      }}
                      height="36px"
                      width="auto"
                      backgroundColor="#F7F7F7"
                      hoverBackgroundColor="#F3F3F3"
                      boxShadow="none"
                      align={"center"}
                    />
                  </div>
                )}
                <div className="flex items-center">
                  {filter.dateValues?.[0] && (
                    <KSpan text={`${toLocaleDate(filter.dateValues[0])} - ${toLocaleDate(filter.dateValues[1])}`} />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-3 items-end">
            {selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY && (
              <KButton
                dataTestId="add-to-discovery-mode-button"
                width="auto"
                padding="8px 16px"
                background="#fff"
                text={`Add to Discovery Mode (${discoveryModeRowKeys.length})`}
                disabled={
                  discoveryModeRowKeys.length === 0 ||
                  discoveryModeSubmitting ||
                  selectedPlatforms.length !== 1 ||
                  !selectedPlatforms[0].value2?.includes(SPOTIFY_PLATFORM)
                }
                onClick={submitDiscoveryMode}
              />
            )}
            <KButton
              width="120px"
              background="black"
              textColor="white"
              text="Apply Filters"
              onClick={() => applySummaryFilter()}
            />
          </div>
        </div>
      )
    }

    return <div className="mb-4 flex"></div>
  }

  const renderTabs = () => {
    return (
      <div className="w-full flex flex-row items-center justify-between gap-3 flex-wrap-reverse">
        <div className="flex gap-4">
          {checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_TOP_100, user?.adminPermissions) && (
            <div
              className="cursor-pointer"
              onClick={() => {
                resetFilters()
                setSelectedTab(TabOptions.STREAMS_TOP_TRACKS)
              }}
              style={{ borderBottom: selectedTab === TabOptions.STREAMS_TOP_TRACKS ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={"Top 100"}
                fontWeight={selectedTab === TabOptions.STREAMS_TOP_TRACKS ? 500 : 400}
                color={selectedTab === TabOptions.STREAMS_TOP_TRACKS ? "#111111" : ""}
              />
            </div>
          )}
          {checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_STREAMS_L3M, user?.adminPermissions) && (
            <div
              className="cursor-pointer"
              onClick={() => {
                resetFilters()
                setSelectedTab(TabOptions.STREAMS_TRACK_MONTHS)
              }}
              style={{ borderBottom: selectedTab === TabOptions.STREAMS_TRACK_MONTHS ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={"Streams Last 12 Months"}
                fontWeight={selectedTab === TabOptions.STREAMS_TRACK_MONTHS ? 500 : 400}
                color={selectedTab === TabOptions.STREAMS_TRACK_MONTHS ? "#111111" : ""}
              />
            </div>
          )}
          {checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_ROYALTY_L3M, user?.adminPermissions) && (
            <div
              className="cursor-pointer"
              onClick={() => {
                resetFilters()
                setSelectedTab(TabOptions.ROYALTY_THREE_MONTHS)
              }}
              style={{ borderBottom: selectedTab === TabOptions.ROYALTY_THREE_MONTHS ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={"Royalty Last 12 Months"}
                fontWeight={selectedTab === TabOptions.ROYALTY_THREE_MONTHS ? 500 : 400}
                color={selectedTab === TabOptions.ROYALTY_THREE_MONTHS ? "#111111" : ""}
              />
            </div>
          )}
          {checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_ROYALTY_SUMMARY, user?.adminPermissions) && (
            <div
              className="cursor-pointer"
              onClick={() => {
                resetFilters()
                setSelectedTab(TabOptions.TRACK_ROYALTY_SUMMARY)
              }}
              style={{ borderBottom: selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={"Track Royalty Summary"}
                fontWeight={selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? 500 : 400}
                color={selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY ? "#111111" : ""}
              />
            </div>
          )}
          {checkAdminPermission(ADMIN_PERMISSIONS.ADMIN_INSIGHTS_STREAM_SUMMARY, user?.adminPermissions) && (
            <div
              className="cursor-pointer"
              onClick={() => {
                resetFilters()
                setSelectedTab(TabOptions.TRACK_STREAM_SUMMARY)
              }}
              style={{ borderBottom: selectedTab === TabOptions.TRACK_STREAM_SUMMARY ? "1px solid #666666" : "" }}
            >
              <KSpan
                text={"Track Stream Summary"}
                fontWeight={selectedTab === TabOptions.TRACK_STREAM_SUMMARY ? 500 : 400}
                color={selectedTab === TabOptions.TRACK_STREAM_SUMMARY ? "#111111" : ""}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex bg-[#fff] h-screen">
      <div className="grow flex justify-center overflow-x-auto p-3 h-screen min-h-0">
        <div className="w-full flex flex-col items-center h-full" style={{}}>
          <div className="pt-5 w-full flex-1 min-h-0 flex flex-col">
            {renderTabs()}
            {renderFilters()}
            {loading && <Loading />}
            {!loading && selectedTab === TabOptions.STREAMS_TOP_TRACKS && (
              <StreamsTopTracks trendTopTracks={trendTopTracks} />
            )}
            {!loading && selectedTab === TabOptions.STREAMS_TRACK_MONTHS && (
              <StreamsTrackMonths
                artistTrendEconomyData={artistTrendEconomyData}
                offset={offset}
                setOffset={setOffset}
                limit={limit}
                selectedRoyaltyType={selectedRoyaltyType}
                setSelectedRoyaltyType={setSelectedRoyaltyType}
                selectedResults={selectedResults}
                setSelectedResults={setSelectedResults}
                hasNext={hasNext}
              />
            )}
            {!loading && selectedTab === TabOptions.ROYALTY_THREE_MONTHS && (
              <RoyaltyThreeMonths
                artistTrendEconomyData={artistTrendEconomyData}
                offset={offset}
                setOffset={setOffset}
                limit={limit}
                selectedRoyaltyType={selectedRoyaltyType}
                setSelectedRoyaltyType={setSelectedRoyaltyType}
                selectedResults={selectedResults}
                setSelectedResults={setSelectedResults}
                hasNext={hasNext}
              />
            )}
            {!loading && selectedTab === TabOptions.TRACK_ROYALTY_SUMMARY && (
              <SummaryTrackRoyalties
                summaryTrackRoyalties={summaryTrackRoyalties}
                handleSort={handleSort}
                sort={sort}
                selectedRowKeys={discoveryModeRowKeys}
                onToggleRows={toggleDiscoveryModeRows}
              />
            )}
            {!loading && selectedTab === TabOptions.TRACK_STREAM_SUMMARY && (
              <SummaryTrackStreams summaryTrackStreams={summaryTrackStreams} handleSort={handleSort} sort={sort} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    options: state.trends.options,
    trendTopTracks: state.trends.trendTopTracks,
    streamsTrackMonths: state.trends.streamsTrackMonths,
    artistTrendEconomyData: state.trends.artistTrendEconomyData,
    summaryTrackRoyalties: state.trends.summaryTrackRoyalties,
    summaryTrackStreams: state.trends.summaryTrackStreams,
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {
  fetchTrendTopTracks,
  fetchStreamsTrackMonths,
  fetchArtistTrendEconomyData,
  fetchSummaryTrackRoyalties,
  fetchSummaryTrackStreams
})(AdminTrends)
